import * as luxon from 'luxon';
import { getSavedLang } from './helpers';
import {
  formatToMaxFourDigitsWithLocale,
  energyBreakdownSuffixWithLocale,
} from './number-format';
import { ICalendarDisplay, KpiEntry, TimeGranularity } from './libs.interfaces';

const ENV = (import.meta as any).env;
const savedLang = getSavedLang() || null;
const browserLanguage =
  savedLang?.split('_')[1] ||
  navigator.language.split('-')[0] ||
  ENV.VITE_APP_LOCALE;

// Locale for number formatting
// If VITE_APP_NUMBER_FORMATTING is set (e.g., "it-CH"), use it. Otherwise, legacy behavior.
// Using a getter to ensure env variable is read at runtime, not at module load time
export function getNumberFormatLocale(): string {
  const env = (import.meta as any).env;
  const envValue = env?.VITE_APP_NUMBER_FORMATTING;
  // Clean up any quotes that might be accidentally included from .env file
  const cleanValue =
    typeof envValue === 'string' ? envValue.replace(/['"]/g, '').trim() : null;
  return cleanValue || browserLanguage || 'en';
}

// For backwards compatibility
export const numberFormatLocale =
  ENV.VITE_APP_NUMBER_FORMATTING || browserLanguage;

const DateTime = luxon.DateTime;

type ApiResponse = {
  energyUnitAndDiver: any;
  pollutionUnitAndDivider: any;
  economicsUnitAndDivider: any;
  rawData: any[];
  data: any[];
  formattedData: any[];
};

// Takes a JavaScript Date object and returns a formatted string in the "yyyy-MM-dd" format.
export const formatDate = (date: Date) =>
  DateTime.fromJSDate(date).toFormat('yyyy-MM-dd'); // e.g., "2023-01-01"

export const formatHumanDate = (date: string) =>
  DateTime.fromJSDate(new Date(date)).toFormat('dd/MM/yyyy'); // e.g., "2023-01-01"

// Takes a String Date with a starting format and returns it in another.
export const formatDateFromFormatToFormat = (
  date: Date,
  format: string,
  toFormat: string
) => {
  const dateElement = date.toString().split('/');
  const checkedInputString = `${
    dateElement[0].length > 1 ? dateElement[0] : `0${dateElement[0]}`
  }/${dateElement[1].length > 1 ? dateElement[1] : `0${dateElement[1]}`}/${
    dateElement[2]
  }`;
  const inputDate = DateTime.fromFormat(checkedInputString, format);
  return inputDate.setLocale('it').toFormat(toFormat); // e.g., "dd MM yyyy" -> "1 August 2023"
};

// Takes a JavaScript Date object and returns a formatted string in the "yyyy-MM-dd" format.
export const formatEndOfWeek = (date: Date) =>
  DateTime.fromJSDate(date).plus({ days: 1 }).toFormat('yyyy-MM-dd'); // e.g., "2023-01-01"

// Takes a JavaScript Date object and returns a formatted string representing the day of the month.
const formatDayNumber = (date: Date) =>
  DateTime.fromJSDate(date).toFormat('dd'); // e.g., "01"

// Takes a JavaScript Date object and returns a formatted string representing the month.
const formatMonthNumber = (date: Date) =>
  DateTime.fromJSDate(date).toFormat('MM'); // e.g., "01"

// Takes a JavaScript Date object and returns a formatted string representing the full month and year.
const formatMonthYear = (date: Date) =>
  DateTime.fromJSDate(date).toFormat('MMMM yyyy'); // e.g., "January 2023"

// Takes a JavaScript Date object and returns a formatted string representing the year.
const formatYear = (date: Date) => DateTime.fromJSDate(date).toFormat('yyyy'); // e.g., "2023"

// Helper functions to format date
export const getFormattedDay = (date: Date) => formatDate(date);
export const getFormattedDayNumber = (date: Date) => formatDayNumber(date);
export const getMonthNumber = (date: Date) => formatMonthNumber(date);
export const getFormattedMonthYear = (date: Date) => formatMonthYear(date);
export const getYear = (date: Date) => formatYear(date);

// Gets the end of the day for a given date, in ISO 8601 format.
export const getFormattedEndOfDay = (date: Date) =>
  DateTime.fromISO(formatDate(date))
    .endOf('day')
    .toFormat("yyyy-MM-dd'T'HH:mm:ss"); // e.g., "2023-01-01T23:59:59"

// Returns the day after the input date
export const getFormattedNextDay = (date: Date) => {
  const nextDay = DateTime.fromJSDate(date).plus({ days: 1 });
  return formatDate(nextDay.toJSDate()); // e.g., "2023-01-02"
};

// Returns the month after the input date
export const getFormattedNextMonth = (date: Date) => {
  const nextMonth = DateTime.fromJSDate(date).plus({ months: 1 });
  return formatDate(nextMonth.toJSDate()); // e.g., "2023-02-01"
};

// Returns the last day of the year for the input date
export const getLastDayOfYear = (date: Date) => {
  const year = DateTime.fromJSDate(date).year + 1;
  const lastDayOfYear = DateTime.fromObject({ year, month: 1, day: 1 });
  return formatDate(lastDayOfYear.toJSDate()); // e.g., "2023-12-31"
};

// Formats date to Italian locale
export const formatDateItalian = (date: string) => {
  const unformattedDate = DateTime.fromISO(date).setLocale('it');
  const formattedDate = unformattedDate.toFormat("d'°' LLLL yyyy"); // e.g., "1° gennaio 2023"
  return formattedDate;
};
// Formats date to Italian locale
export const formatMonthAndYear = (date: string, short = false) => {
  const formattedDate = DateTime.fromISO(date).toFormat(
    short ? 'LLL yy' : 'LLLL yyyy'
  ); // e.g., Jan 2023 || Gennaio 2023
  return formattedDate;
};

// Calculates the percentage of value1 in the sum of value1 and value2, or in totalAmount if provided.
export const calculatePercentage = (
  value1: any,
  value2: any,
  totalAmount?: any
) => {
  const showedValue = parseFloat(value1);
  const helperValue = parseFloat(value2);
  const total = totalAmount ? totalAmount : showedValue + helperValue;
  const percentage = (showedValue / total) * 100; // e.g., 50
  return percentage;
};

// Converts an ISO 8601 date string to the format "yyyy-MM-dd'T'HH:mm:ss".
export const formatStringToDate = (input: string): string => {
  try {
    const date = DateTime.fromISO(input);
    return date.toFormat('yyyy-MM-dd'); // e.g., "2023-01-01T00:00:00"
  } catch (err: any) {
    // console.warn(err);
    return '';
  }
};

// Gets the end of the month for a given date, in ISO 8601 format.
export const getFormattedEndOfMonth = (input: Date): string => {
  const nextDate = DateTime.fromJSDate(input).plus({ months: 1 });
  const nextDateMonth = nextDate.month;
  const nextDateYear = nextDate.year;
  const firstDayOfNextMonth = DateTime.fromObject({
    year: nextDateYear,
    month: nextDateMonth,
    day: 1,
  });

  return firstDayOfNextMonth.toFormat("yyyy-MM-dd'T'HH:mm:ss"); // e.g., "2023-01-31T23:59:59"
};

export const getTimeGranularityOverview = (
  calendarDisplay: ICalendarDisplay
) => {
  let timeGranularity = '1day';
  switch (calendarDisplay) {
    case 'week': {
      timeGranularity = '1week';
      break;
    }
    case 'month': {
      timeGranularity = '1month';
      break;
    }
    case 'year': {
      timeGranularity = '1year';
      break;
    }
    default: {
      return timeGranularity;
    }
  }
  return timeGranularity;
};

export const getTimeGranularity = (
  calendarDisplay: ICalendarDisplay,
  minTimeResolution: string | null
) => {
  const basicTimeGranularity =
    ENV.VITE_APP_NAME === 'kwick' ? '15minute' : '1hour';

  let timeGranularity = minTimeResolution || basicTimeGranularity;

  switch (calendarDisplay) {
    case 'week': {
      timeGranularity = '1day';
      break;
    }
    case 'month': {
      timeGranularity = '1day';
      break;
    }
    case 'year': {
      timeGranularity = '1month';
      break;
    }
    default: {
      return timeGranularity;
    }
  }
  return timeGranularity;
};

// Normalizes dateFrom/dateTo to period boundaries when timeGranularity is 1month or 1year.
// - For 1month: dateFrom -> startOf('month'), dateTo -> startOf('next month')
// - For 1year:  dateFrom -> startOf('year'),  dateTo -> startOf('next year')
export function normalizeDateRangeForGranularity<
  T extends { timeGranularity?: string; dateFrom?: any; dateTo?: any }
>(input: T): T {
  try {
    const pg = (input as any)?.timeGranularity;
    const hasDates =
      (input as any) && (input as any).dateFrom && (input as any).dateTo;
    if (!pg || !hasDates) return input;

    const from = DateTime.fromISO(String((input as any).dateFrom));
    const to = DateTime.fromISO(String((input as any).dateTo));
    if (!from.isValid || !to.isValid) return input;

    // Handle 1day rule
    if (pg === '1day') {
      const startDay = from.startOf('day');
      const endDay = to.startOf('day');
      const daysCount = Math.floor(endDay.diff(startDay, 'days').days) + 1;
      if (daysCount > 8) {
        const mStart = from.startOf('month');
        const mEnd = mStart.plus({ months: 1 }).startOf('month');
        return {
          ...(input as any),
          dateFrom: mStart.toFormat('yyyy-MM-dd'),
          dateTo: mEnd.toFormat('yyyy-MM-dd'),
        } as T;
      }
      return {
        ...(input as any),
        dateFrom: startDay.toFormat('yyyy-MM-dd'),
        dateTo: endDay.toFormat('yyyy-MM-dd'),
      } as T;
    }

    // Handle 1month/1year alignment
    if (pg === '1month' || pg === '1year') {
      const unit = pg === '1month' ? 'month' : 'year';
      const start = from.startOf(unit as any);
      const end = to.startOf(unit as any);

      // Exception for 12 months in 1month: expand to full year based on dateFrom
      if (pg === '1month') {
        const monthsCount =
          (end.year - start.year) * 12 + (end.month - start.month) + 1;
        if (monthsCount >= 12) {
          const yStart = from.startOf('year');
          const yEnd = yStart.plus({ years: 1 }).startOf('year');
          return {
            ...(input as any),
            dateFrom: yStart.toFormat('yyyy-MM-dd'),
            dateTo: yEnd.toFormat('yyyy-MM-dd'),
          } as T;
        }
      }

      return {
        ...(input as any),
        dateFrom: start.toFormat('yyyy-MM-dd'),
        dateTo: end.toFormat('yyyy-MM-dd'),
      } as T;
    }

    // Default: return untouched
    return input;
  } catch {
    return input;
  }
}

export function capitalizeFirstLetter(str: string) {
  // converting first letter to uppercase
  try {
    return str.charAt(0).toUpperCase() + str.slice(1);
  } catch (err: any) {
    // console.warn(err);
    // console.warn(`returing string [ ${str} ]`);
    return str;
  }
}
export const formatString = (
  inputString: string,
  type: 'number' | 'unit',
  widgetType?: 'stats' | 'card'
): string | number => {
  let letterIndex = 0;
  let numericalPart = '';
  let unitPart = '';

  // Find the position of the first letter (a-z) in the string
  try {
    letterIndex = inputString.search(/[a-z]/i);
  } catch (err: any) {
    // console.warn(err);
  }

  // Extract the numerical part before the letter
  try {
    numericalPart = inputString.substring(0, letterIndex);
  } catch (err: any) {
    // console.warn(err);
  }

  // Extract the unit part after the letter
  try {
    unitPart = inputString.substring(letterIndex);
  } catch (err: any) {
    // console.warn(err);
  }

  if (type === 'number') {
    // return convertAndRound(numericalPart, widgetType);
    return numericalPart;
  }
  return unitPart;
};
export function convertAndRoundPercentage(str: any): number {
  if (typeof str !== 'string') {
    return 0;
  } else {
    // Replace comma with period
    const replacedStr = str.replace(',', '.');
    // Convert to float
    const num = parseFloat(replacedStr);
    // Round and return
    return Math.round(num);
  }
}
export function convertAndRound(str: any, type?: 'card' | 'stats'): number {
  if (typeof str !== 'string') {
    return 0;
  } else {
    // Replace comma with period
    const replacedStr = str.replace(',', '.');
    // Convert to float
    const num = parseFloat(replacedStr);
    if (type === 'card') {
      return Math.round(num);
    }
    if (num >= 100) {
      return Math.round(num);
    }
    // Round and return
    else return num;
  }
}
export function extractNumber(inputString: string): string {
  try {
    const numberMatch = inputString.match(/[+-]?([0-9]*[.])?[0-9]+/);
    return numberMatch ? numberMatch[0] : '';
  } catch (err: any) {
    // console.warn(err);
    return '';
  }
}

function isNumeric(value: any): boolean {
  return typeof value === 'number' && !isNaN(value);
}

function aggregateValues(entries: any[]): any[] {
  const aggregatedEntries: { [key: string]: any } = {};

  entries.forEach((entry) => {
    if (!aggregatedEntries[entry.timestamp]) {
      aggregatedEntries[entry.timestamp] = { ...entry };
    } else {
      Object.keys(entry).forEach((key) => {
        if (isNumeric(entry[key])) {
          aggregatedEntries[entry.timestamp][key] =
            (aggregatedEntries[entry.timestamp][key] || 0) + entry[key];
        }
      });
    }
  });

  return Object.values(aggregatedEntries);
}

export function communitiesAggregateData(response: ApiResponse): ApiResponse {
  return {
    ...response,
    rawData: aggregateValues(response.rawData),
    data: aggregateValues(response.data),
    formattedData: aggregateValues(
      response.formattedData.map((item) => item.localized)
    ).map((localized, index) => {
      return {
        ...response.formattedData[index],
        localized,
      };
    }),
  };
}

export function checkKpiType(type: string, value: any): string {
  if (value === 0) {
    return '0.00';
  }

  try {
    if (type === 'energy' || type === 'ancillary') {
      return formatToMaxFourDigits(value);
    } else {
      // For economics and other types, use formatToMaxFourDigits with isEconomics=true
      // to ensure proper thousand separators and decimal formatting
      return formatToMaxFourDigits(value, true);
    }
  } catch (err: any) {
    console.warn(err);
    return '-.-';
  }
}

export function formatToMaxFourDigits(
  num: number | string,
  isEconomics = false
): string {
  // Locale is resolved at call time so the env variable is available; the
  // actual formatting lives in number-format.ts (pure, unit-tested).
  return formatToMaxFourDigitsWithLocale(num, isEconomics, getNumberFormatLocale());
}

/** " | <value> <unit>" suffix for stats-overview breakdown rows (FLEXO-6133 #2). */
export function energyBreakdownSuffix(valueNumber: number | null | undefined, unit: string): string {
  return energyBreakdownSuffixWithLocale(valueNumber, unit, getNumberFormatLocale());
}

/**
 * Generates all expected timestamps for a given date range and granularity.
 * Returns an array of ISO timestamp strings.
 */
function generateExpectedTimestamps(
  dateFrom: string,
  dateTo: string,
  granularity: TimeGranularity
): string[] {
  const timestamps: string[] = [];

  // For sub-day granularities, normalize to start of day
  // For day+ granularities, use the date as-is (already normalized by caller)
  let current = DateTime.fromISO(dateFrom).startOf('day');
  const end = DateTime.fromISO(dateTo).startOf('day');

  // Determine the step based on granularity
  const stepMap: Record<TimeGranularity, { unit: luxon.DurationUnit; amount: number }> = {
    '15min': { unit: 'minutes', amount: 15 },
    '1hour': { unit: 'hours', amount: 1 },
    '1day': { unit: 'days', amount: 1 },
    '1week': { unit: 'weeks', amount: 1 },
    '1month': { unit: 'months', amount: 1 },
    '1year': { unit: 'years', amount: 1 },
  };

  const step = stepMap[granularity] || { unit: 'days', amount: 1 };

  while (current < end) {
    timestamps.push(current.toISO() as string);
    current = current.plus({ [step.unit]: step.amount });
  }

  return timestamps;
}

/**
 * Fills missing timestamps in KPI data with null values.
 * Preserves the original data structure from the API response.
 *
 * @param data - Array of KPI entries from the API
 * @param dateFrom - Start date in YYYY-MM-DD format
 * @param dateTo - End date in YYYY-MM-DD format (exclusive)
 * @param granularity - Time granularity (1day, 1hour, etc.)
 * @returns Array of KPI entries with missing timestamps filled with null values
 */
export function fillMissingTimestamps(
  data: KpiEntry[] | null | undefined,
  dateFrom: string,
  dateTo: string,
  granularity: TimeGranularity
): KpiEntry[] {
  // Return empty array if no data
  if (!data || data.length === 0) {
    return [];
  }

  // Get the template from the first entry to preserve structure
  const template = data[0];
  if (!template?.kpi) {
    return data;
  }

  // Generate all expected timestamps
  const expectedTimestamps = generateExpectedTimestamps(dateFrom, dateTo, granularity);

  // Determine normalization format based on granularity
  // Sub-day granularities need full ISO timestamp as key to avoid collapsing all entries to the same day
  const isSubDay = granularity === '15min' || granularity === '1hour';
  const normalizeKey = (dt: typeof DateTime.prototype): string => {
    if (isSubDay) return dt.toFormat("yyyy-MM-dd'T'HH:mm");
    if (granularity === '1year') return dt.startOf('year').toFormat('yyyy-MM-dd');
    if (granularity === '1month') return dt.startOf('month').toFormat('yyyy-MM-dd');
    return dt.startOf('day').toFormat('yyyy-MM-dd');
  };

  // Create a map of existing data by normalized timestamp for O(1) lookup
  const dataByTimestamp = new Map<string, KpiEntry>();
  for (const entry of data) {
    const dt = DateTime.fromISO(entry.timestamp);
    dataByTimestamp.set(normalizeKey(dt), entry);
  }

  // Build result array with all timestamps
  const result: KpiEntry[] = [];

  for (const expectedTs of expectedTimestamps) {
    const normalizedKey = normalizeKey(DateTime.fromISO(expectedTs));

    // Find existing entry for this timestamp
    const existingEntry = dataByTimestamp.get(normalizedKey);

    if (existingEntry) {
      // Use the existing entry as-is
      result.push(existingEntry);
    } else {
      // Create a placeholder entry with null values
      const placeholderEntry: KpiEntry = {
        timestamp: expectedTs,
        unixTimestamp: Math.floor(DateTime.fromISO(expectedTs).toSeconds()),
        flexoId: template.flexoId,
        organizationId: template.organizationId,
        aggregationLevel: template.aggregationLevel,
        kpi: template.kpi.map((kpiItem) => ({
          ...kpiItem,
          value: null, // Use null to indicate missing data (not 0 to avoid false positives)
        })),
      };
      result.push(placeholderEntry);
    }
  }

  return result;
}
